import React from 'react';
import { History as HistoryIcon } from 'lucide-react';
import { getHistory } from '../../../lib/history';

interface HistorySectionProps {
    contactId: string;
    userId: string;
}

export default function HistorySection({ contactId, userId }: HistorySectionProps) {
    const [history, setHistory] = React.useState<any[]>([]);
    const [historyLoading, setHistoryLoading] = React.useState(false);
    const [showAllHistory, setShowAllHistory] = React.useState(false);
    const [detailOpen, setDetailOpen] = React.useState<null | any>(null);

    // Load contact history when id changes
    React.useEffect(() => {
        let active = true;
        (async () => {
            if (!contactId) return;
            setHistoryLoading(true);
            try {
                const entries = await getHistory(contactId);
                if (active) setHistory(entries);
            } catch (e) {
                console.error('Failed to load history', e);
            } finally {
                if (active) setHistoryLoading(false);
            }
        })();
        return () => { active = false; };
    }, [contactId]);

    // Listen for explicit history refresh requests (from child sections)
    React.useEffect(() => {
        const handler = (e: CustomEvent) => {
            const targetId = e.detail?.contactId;
            if (!targetId || targetId !== contactId) return;
            (async () => {
                if (!targetId) return;
                setHistoryLoading(true);
                try {
                    const entries = await getHistory(targetId);
                    setHistory(entries);
                } catch (e) {
                    console.error('Failed to refresh history', e);
                } finally {
                    setHistoryLoading(false);
                }
            })();
        };
        window.addEventListener('gv:refreshHistory', handler as EventListener);
        return () => window.removeEventListener('gv:refreshHistory', handler as EventListener);
    }, [contactId]);

    const relTime = (iso?: string) => {
        if (!iso) return '';
        const d = new Date(iso);
        const now = new Date();
        const msPerDay = 24 * 60 * 60 * 1000;
        const startOfDay = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate());
        const diffDays = Math.round((startOfDay(now).getTime() - startOfDay(d).getTime()) / msPerDay);
        if (diffDays === 0) return 'Today';
        if (diffDays === 1) return 'Yesterday';
        if (diffDays > 1 && diffDays <= 7) return `${diffDays} days ago`;
        return d.toLocaleDateString();
    };

    const friendly = (h: any) => {
        const a = String(h.action || '').toLowerCase();
        const d = h.details || {};
        const name = d.name || d.title || d.company || d.contact || undefined;
        if (a === 'event_added') return name ? `Event added: ${name}` : 'Event added';
        if (a === 'event_updated') return name ? `Event updated: ${name}` : 'Event updated';
        if (a === 'company_seeded') return name ? `Company seeded: ${name}` : 'Company seeded';
        if (a === 'company_added') return name ? `Company added: ${name}` : 'Company added';
        if (a === 'company_updated') return name ? `Company updated: ${name}` : 'Company updated';
        if (a === 'company_deleted') return name ? `Company removed: ${name}` : 'Company removed';
        if (a === 'relationship_created') {
            const rel = d.after?.relation || d.relation || undefined;
            if (name && rel) return `Became ${rel} with ${name}`;
            if (name) return `Relationship created with ${name}`;
            return 'Relationship created';
        }
        if (a === 'relationship_updated') {
            const beforeRel = d.before?.relation || undefined;
            const afterRel = d.after?.relation || undefined;
            if (name && beforeRel && afterRel && beforeRel !== afterRel) return `Relationship changed with ${name}: ${beforeRel} → ${afterRel}`;
            if (name && afterRel) return `Relationship updated: ${name} (${afterRel})`;
            if (name) return `Relationship updated: ${name}`;
            return 'Relationship updated';
        }
        if (a === 'relationship_deleted') {
            const rel = d.relation || d.before?.relation || undefined;
            if (name && rel) return `No longer ${rel} with ${name}`;
            if (name) return `Relationship removed: ${name}`;
            return 'Relationship removed';
        }
        if (a === 'overview_generated') return 'Overview updated';
        if (a === 'note_added') return 'Note added';
        // Fallback
        return String(h.action || '').replace(/_/g, ' ');
    };

    return (
        <>
            <div id="contact-history" className="bg-gray-900 rounded-xl">
                <div className="w-full flex items-center justify-between px-5 py-4 rounded-t-xl">
                    <div className="flex items-center space-x-2">
                        <HistoryIcon className="w-5 h-5 text-gray-400" />
                        <h3 className="text-lg font-semibold text-white">History</h3>
                    </div>
                </div>
                <div className="px-5 pb-5">
                    <div className="bg-gray-800 rounded-lg p-3 border border-gray-700">
                        {historyLoading ? (
                            <p className="text-sm text-gray-400">Loading history…</p>
                        ) : history.length === 0 ? (
                            <p className="text-sm text-gray-400">No history yet.</p>
                        ) : (
                            <>
                                <ul className="divide-y divide-gray-700">
                                    {(showAllHistory ? history : history.slice(0, 1)).map((h: any) => {
                                        const when = relTime(h.at);
                                        const title = friendly(h);
                                        const extra: string | null = (() => {
                                            const d = h.details || {};
                                            if (d.when) {
                                                try { return `When: ${new Date(d.when).toLocaleDateString()}`; } catch { return null; }
                                            }
                                            if (d.where) return `Where: ${d.where}`;
                                            return null;
                                        })();
                                        const clickable = !!(h.details && (h.details.before || h.details.after));
                                        return (
                                            <li key={h.id} className="py-2 text-sm">
                                                <button
                                                    type="button"
                                                    onClick={() => clickable && setDetailOpen(h)}
                                                    className={`w-full text-left ${clickable ? 'hover:bg-gray-800/60 rounded-md px-2 py-1 -mx-2 transition-colors' : ''}`}
                                                >
                                                    <div className="text-gray-200 flex items-center justify-between leading-5">
                                                        <div className="font-medium">{title}</div>
                                                        <div className="text-xs text-gray-400">{when}</div>
                                                    </div>
                                                    {extra && (
                                                        <div className="mt-0.5 text-xs text-gray-400 leading-5">{extra}</div>
                                                    )}
                                                    {clickable && (
                                                        <div className="mt-0.5 text-[11px] text-gray-500">Click to view prior state</div>
                                                    )}
                                                </button>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </>
                        )}
                    </div>
                    {history.length > 1 && (
                        <div className="mt-2 flex justify-center">
                            <button
                                onClick={() => setShowAllHistory(v => !v)}
                                className="px-3 py-1.5 text-xs rounded-md border border-transparent bg-gray-900/70 text-gray-300 hover:bg-gray-700 hover:text-white hover:border-gray-700 active:border-gray-700 focus-visible:border-gray-700 focus:outline-none focus-visible:ring-1 focus-visible:ring-gray-600 transition-colors"
                            >
                                {showAllHistory ? 'View less' : 'View more'}
                            </button>
                        </div>
                    )}
                </div>
            </div>
            {/* Drill-in modal for history item with before/after */}
            {detailOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <div className="absolute inset-0 bg-black/60" onClick={() => setDetailOpen(null)} />
                    <div className="relative w-full sm:max-w-md bg-gray-900 border border-gray-700 rounded-xl p-5 shadow-2xl">
                        <div className="text-white font-semibold mb-2">{friendly(detailOpen)}</div>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                            <div>
                                <div className="text-gray-400 text-xs mb-1">Before</div>
                                <div className="space-y-1 text-gray-200">
                                    <div>Relation: {detailOpen?.details?.before?.relation || '—'}</div>
                                    <div>Where: {detailOpen?.details?.before?.whereMet || '—'}</div>
                                    <div>When: {detailOpen?.details?.before?.whenMet ? new Date(detailOpen.details.before.whenMet).toLocaleDateString() : '—'}</div>
                                    <div className="text-xs text-gray-400">Notes: {detailOpen?.details?.before?.notes || '—'}</div>
                                </div>
                            </div>
                            <div>
                                <div className="text-gray-400 text-xs mb-1">After</div>
                                <div className="space-y-1 text-gray-200">
                                    <div>Relation: {detailOpen?.details?.after?.relation || '—'}</div>
                                    <div>Where: {detailOpen?.details?.after?.whereMet || '—'}</div>
                                    <div>When: {detailOpen?.details?.after?.whenMet ? new Date(detailOpen.details.after.whenMet).toLocaleDateString() : '—'}</div>
                                    <div className="text-xs text-gray-400">Notes: {detailOpen?.details?.after?.notes || '—'}</div>
                                </div>
                            </div>
                        </div>
                        <div className="mt-4 flex justify-end">
                            <button onClick={() => setDetailOpen(null)} className="px-3 py-1.5 text-sm rounded-md border border-gray-700 text-gray-300 hover:bg-gray-700">Close</button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}